import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, Loader2, KeyRound, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import AuthShell from '../components/AuthShell';
import { api } from '../lib/api';

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: email, 2: code, 3: new password
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  async function handleRequest(e) {
    e.preventDefault();
    if (!email.trim()) return toast.error('Enter your email');
    setLoading(true);
    try {
      await api('/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ email: email.toLowerCase().trim() }),
      });
      toast.success('If the email exists, a reset code was sent');
      setStep(2);
    } catch (err) {
      toast.error(err.message || 'Failed to request reset');
    } finally {
      setLoading(false);
    }
  }

  async function handleVerifyCode(e) {
    e.preventDefault();
    if (code.length !== 6) return toast.error('Enter the 6-digit code');
    setLoading(true);
    try {
      const data = await api('/auth/verify-reset-code', {
        method: 'POST',
        body: JSON.stringify({ email: email.toLowerCase().trim(), code }),
      });
      setResetToken(data.reset_token);
      setStep(3);
    } catch (err) {
      toast.error(err.message || 'Invalid code');
    } finally {
      setLoading(false);
    }
  }

  async function handleResetPassword(e) {
    e.preventDefault();
    if (newPassword.length < 8) return toast.error('Password must be at least 8 characters');
    if (newPassword !== confirmPassword) return toast.error('Passwords do not match');
    setLoading(true);
    try {
      await api('/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({ token: resetToken, new_password: newPassword }),
      });
      toast.success('Password reset! Please log in.');
      navigate('/app/login', { state: { email } });
    } catch (err) {
      toast.error(err.message || 'Reset failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthShell backTo="/app/login">
      <div className="w-16 h-16 rounded-full bg-[#FFE5DD] flex items-center justify-center mb-6">
        <KeyRound className="w-8 h-8 text-[#C8553D]" />
      </div>
      <h1 className="text-3xl font-serif font-bold text-[#2B2D42]" style={{ fontFamily: 'Playfair Display, serif' }}>
        {step === 1 && 'Forgot Password'}
        {step === 2 && 'Enter Reset Code'}
        {step === 3 && 'New Password'}
      </h1>
      <p className="text-[#6D6875] mt-2 mb-8">
        {step === 1 && 'Enter your email and we\'ll send a 6-digit reset code'}
        {step === 2 && `Enter the code sent to ${email}`}
        {step === 3 && 'Choose a new password (8+ characters)'}
      </p>

      {step === 1 && (
        <form onSubmit={handleRequest} className="space-y-5" data-testid="forgot-password-step1-form">
          <div className="flex items-center bg-white border border-[#F4F1DE] rounded-lg px-4 py-3 focus-within:border-[#E07A5F]">
            <Mail className="w-5 h-5 text-[#B5B5B5]" />
            <input
              type="email"
              data-testid="forgot-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              className="flex-1 ml-3 outline-none bg-transparent text-[#2B2D42]"
            />
          </div>
          <button type="submit" disabled={loading} data-testid="forgot-submit-button"
            className="w-full bg-[#C8553D] hover:bg-[#9E3A26] disabled:opacity-60 text-white font-bold py-4 rounded-full flex items-center justify-center gap-2 shadow-lg shadow-[#C8553D]/30">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Send Code'}
          </button>
        </form>
      )}

      {step === 2 && (
        <form onSubmit={handleVerifyCode} className="space-y-5" data-testid="forgot-password-step2-form">
          <input
            type="text"
            inputMode="numeric"
            maxLength={6}
            data-testid="forgot-code-input"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="000000"
            className="w-full text-center text-3xl font-bold tracking-[0.5em] bg-white border-2 border-[#F4F1DE] rounded-lg py-4 outline-none focus:border-[#E07A5F] text-[#2B2D42]"
          />
          <button type="submit" disabled={loading || code.length !== 6} data-testid="forgot-verify-code-button"
            className="w-full bg-[#C8553D] hover:bg-[#9E3A26] disabled:opacity-60 text-white font-bold py-4 rounded-full flex items-center justify-center gap-2 shadow-lg shadow-[#C8553D]/30">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Verify Code'}
          </button>
        </form>
      )}

      {step === 3 && (
        <form onSubmit={handleResetPassword} className="space-y-5" data-testid="forgot-password-step3-form">
          <div className="flex items-center bg-white border border-[#F4F1DE] rounded-lg px-4 py-3 focus-within:border-[#E07A5F]">
            <Lock className="w-5 h-5 text-[#B5B5B5]" />
            <input
              type={showPassword ? 'text' : 'password'}
              data-testid="forgot-new-password-input"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="New password"
              autoComplete="new-password"
              className="flex-1 ml-3 outline-none bg-transparent text-[#2B2D42]"
            />
            <button type="button" onClick={() => setShowPassword(!showPassword)} className="text-[#B5B5B5]">
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          <div className="flex items-center bg-white border border-[#F4F1DE] rounded-lg px-4 py-3 focus-within:border-[#E07A5F]">
            <Lock className="w-5 h-5 text-[#B5B5B5]" />
            <input
              type={showPassword ? 'text' : 'password'}
              data-testid="forgot-confirm-password-input"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              autoComplete="new-password"
              className="flex-1 ml-3 outline-none bg-transparent text-[#2B2D42]"
            />
          </div>
          <button type="submit" disabled={loading} data-testid="forgot-reset-button"
            className="w-full bg-[#C8553D] hover:bg-[#9E3A26] disabled:opacity-60 text-white font-bold py-4 rounded-full flex items-center justify-center gap-2 shadow-lg shadow-[#C8553D]/30">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Reset Password'}
          </button>
        </form>
      )}

      <div className="mt-8 text-center">
        <Link to="/app/login" className="text-sm font-bold text-[#C8553D]" data-testid="forgot-back-to-login">
          Back to login
        </Link>
      </div>
    </AuthShell>
  );
}
